import type {
	CreateAutoApplicationSettingRequest,
	CreateAutoApplicationSettingResponse,
	FetchAutoApplicationSettingResponse,
	FetchAutoApplicationSettingsQuery,
	FetchAutoApplicationSettingsResponse,
	UpdateAutoApplicationSettingRequest,
	UpdateAutoApplicationSettingResponse,
} from '../../http';
import {
	createAutoApplicationSetting,
	fetchAutoApplicationSetting,
	fetchAutoApplicationSettings,
	updateAutoApplicationSetting,
} from '../../http';
import {useMutation, useQuery, useQueryClient, type UseQueryOptions} from 'react-query';

const DEFAULT_PER_PAGE = 25;

export type AutoApplicationSettingsQuery = Omit<FetchAutoApplicationSettingsQuery, 'filters[search]'> & {
	search?: string;
};

function buildListQuery(query?: AutoApplicationSettingsQuery): FetchAutoApplicationSettingsQuery {
	const {search, ...rest} = query ?? {};

	return {
		per_page: DEFAULT_PER_PAGE,
		...rest,
		...(search ? {'filters[search]': search} : {}),
	};
}

function invalidateAutoApplicationQueries(queryClient: ReturnType<typeof useQueryClient>) {
	queryClient.invalidateQueries({queryKey: ['vacancies']});
	queryClient.invalidateQueries({queryKey: ['vacancy']});
	queryClient.invalidateQueries({queryKey: ['auto-application-settings']});
	queryClient.invalidateQueries({queryKey: ['auto-application-setting']});
}

export function useFetchAutoApplicationSettings(
	query?: AutoApplicationSettingsQuery,
	options?: UseQueryOptions<FetchAutoApplicationSettingsResponse>,
) {
	const q = buildListQuery(query);

	return useQuery<FetchAutoApplicationSettingsResponse>({
		queryKey: ['auto-application-settings', q],
		keepPreviousData: true,
		queryFn: async ({signal}) => {
			const {data} = await fetchAutoApplicationSettings(q, signal);
			return data;
		},
		...options,
	});
}

export function useFetchAutoApplicationSetting(
	id: number,
	options?: UseQueryOptions<FetchAutoApplicationSettingResponse>,
) {
	return useQuery<FetchAutoApplicationSettingResponse>({
		queryKey: ['auto-application-setting', id],
		queryFn: async ({signal}) => {
			const {data} = await fetchAutoApplicationSetting(id, signal);
			return data;
		},
		...options,
	});
}

export function useCreateAutoApplicationSetting() {
	const queryClient = useQueryClient();

	return useMutation<CreateAutoApplicationSettingResponse, unknown, CreateAutoApplicationSettingRequest>({
		mutationFn: async (body) => {
			const {data} = await createAutoApplicationSetting(body);
			return data;
		},
		onSuccess: () => {
			invalidateAutoApplicationQueries(queryClient);
		},
	});
}

export function useUpdateAutoApplicationSetting() {
	const queryClient = useQueryClient();

	return useMutation<
		UpdateAutoApplicationSettingResponse,
		unknown,
		{id: number; body: UpdateAutoApplicationSettingRequest}
	>({
		mutationFn: async ({id, body}) => {
			const {data} = await updateAutoApplicationSetting(id, body);
			return data;
		},
		onSuccess: () => {
			invalidateAutoApplicationQueries(queryClient);
		},
	});
}
